import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBook,
  FaPlus,
  FaTrash,
  FaSave,
  FaSpinner,
  FaInfoCircle,
} from "react-icons/fa";
import CorrectAnswersInput from "./CorrectAnswersInput";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type TaskType =
  | "BEISPIEL_SATZ"
  | "SATZBAU"
  | "ERGANZUNGEN"
  | "HOREN"
  | "SCHREIBEN";

type Task = {
  taskType: TaskType;
  taskOrder: number;
  taskDescription: string;
  correctAnswer: string;
  audioReference: string;
  points: number;
};

const taskTypeLabels: Record<TaskType, string> = {
  BEISPIEL_SATZ: "Beispiel Satz (Example Sentences)",
  SATZBAU: "Satzbau (Sentence Construction)",
  ERGANZUNGEN: "Ergänzungen (Fill in the Blanks)",
  HOREN: "Hören (Listening)",
  SCHREIBEN: "Schreiben (Writing)",
};

const AdminCreateHomeworkPage: React.FC = () => {
  const navigate = useNavigate();

  const [level, setLevel] = useState<string>("A1");
  const [chapterNumber, setChapterNumber] = useState<number>(1);
  const [title, setTitle] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const [tasks, setTasks] = useState<Task[]>([
    {
      taskType: "SCHREIBEN",
      taskOrder: 1,
      taskDescription: "",
      correctAnswer: "",
      audioReference: "",
      points: 20,
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Auto-generate title based on chapter number
  React.useEffect(() => {
    setTitle(`Hausaufgabe - ${chapterNumber}, Kapitel - ${chapterNumber}`);
  }, [chapterNumber]);

  const addTask = () => {
    const newTask: Task = {
      taskType: "SCHREIBEN",
      taskOrder: tasks.length + 1,
      taskDescription: "",
      correctAnswer: "",
      audioReference: "",
      points: 5,
    };
    setTasks([...tasks, newTask]);
  };

  const removeTask = (index: number) => {
    if (tasks.length === 1) {
      alert("Homework must have at least one task");
      return;
    }
    const newTasks = tasks.filter((_, i) => i !== index);
    newTasks.forEach((task, idx) => {
      task.taskOrder = idx + 1;
    });
    setTasks(newTasks);
  };

  const updateTask = (index: number, field: keyof Task, value: any) => {
    const newTasks = [...tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setTasks(newTasks);
  };

  const getTotalPoints = () => {
    return tasks.reduce((sum, task) => sum + (task.points || 0), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const totalPoints = getTotalPoints();
    if (totalPoints !== 20) {
      setError(`Total points must equal 20. Current total: ${totalPoints}`);
      return;
    }

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      if (!task.taskDescription.trim()) {
        setError(`Task ${i + 1}: Description is required`);
        return;
      }

      if (
        (task.taskType === "HOREN" || task.taskType === "ERGANZUNGEN") &&
        !task.correctAnswer.trim()
      ) {
        setError(
          `Task ${i + 1}: Correct answer is required for ${
            task.taskType === "HOREN" ? "Hören" : "Ergänzungen"
          } tasks`
        );
        return;
      }

      if (task.points < 1) {
        setError(`Task ${i + 1}: Points must be at least 1`);
        return;
      }
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/homework`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          level,
          chapterNumber,
          title,
          instructions: instructions.trim() || null,
          tasks: tasks.map((task) => ({
            taskType: task.taskType,
            taskOrder: task.taskOrder,
            taskDescription: task.taskDescription,
            correctAnswer:
              task.taskType === "HOREN" || task.taskType === "ERGANZUNGEN"
                ? task.correctAnswer
                : null,
            audioReference:
              task.taskType === "HOREN" ? task.audioReference : null,
            points: task.points,
          })),
        }),
      });

      if (response.ok) {
        navigate("/admin/homework");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to create homework");
      }
    } catch (err) {
      console.error("Error creating homework:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaBook />}
        title="Create Homework"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Homework", to: "/admin/homework" },
          { label: "Create" },
        ]}
      />

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Info Card */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            Important Information
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Each homework must have a total of <strong>20 points</strong>{" "}
                distributed across all tasks
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Instructions are <strong>level-specific</strong> (same for all
                14 chapters in a level)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                For <strong>Hören (Listening)</strong> and{" "}
                <strong>Ergänzungen (Fill in the Blanks)</strong>, use the "Add
                Answer" button to add each correct answer
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Scoring is <strong>proportional</strong>: If a task has 5
                questions and 5 points, each correct answer = 1 point
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Other task types (Beispiel Satz, Satzbau, Schreiben) will be{" "}
                <strong>evaluated by AI</strong>
              </span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Level *
                </label>
                <select
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                  style={{ fontSize: "16px" }}
                  required
                >
                  <option value="A1">A1</option>
                  <option value="A2">A2</option>
                  <option value="B1">B1</option>
                  <option value="B2">B2</option>
                  <option value="C1">C1</option>
                  <option value="C2">C2</option>
                </select>
              </div>

              {/* Chapter Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Chapter Number * (1-14)
                </label>
                <input
                  type="number"
                  min="1"
                  max="14"
                  value={chapterNumber}
                  onChange={(e) => setChapterNumber(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                  style={{ fontSize: "16px" }}
                  required
                />
              </div>
            </div>

            {/* Title (Auto-generated) */}
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
            </div>

            {/* Instructions */}
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Instructions (Level-specific, optional)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={4}
                placeholder="Enter instructions for this level (same for all 14 chapters)..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all resize-none"
                style={{ fontSize: "16px" }}
              />
            </div>
          </div>

          {/* Tasks Section */}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Tasks ({tasks.length})
              </h2>
              <div className="flex items-center gap-4">
                <span
                  className={`text-sm font-semibold ${
                    getTotalPoints() === 20 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  Total Points: {getTotalPoints()} / 20
                </span>
                <button
                  type="button"
                  onClick={addTask}
                  className="flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm font-semibold"
                >
                  <FaPlus />
                  Add Task
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {tasks.map((task, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Task {index + 1}
                    </h3>
                    {tasks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTask(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Task Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Task Type *
                      </label>
                      <select
                        value={task.taskType}
                        onChange={(e) =>
                          updateTask(
                            index,
                            "taskType",
                            e.target.value as TaskType
                          )
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                        style={{ fontSize: "16px" }}
                        required
                      >
                        {Object.entries(taskTypeLabels).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Points */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Points *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={task.points}
                        onChange={(e) =>
                          updateTask(index, "points", Number(e.target.value))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                        style={{ fontSize: "16px" }}
                        required
                      />
                    </div>
                  </div>

                  {/* Task Description */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Task Description *
                    </label>
                    <textarea
                      value={task.taskDescription}
                      onChange={(e) =>
                        updateTask(index, "taskDescription", e.target.value)
                      }
                      rows={3}
                      placeholder="Enter task description..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all resize-none"
                      style={{ fontSize: "16px" }}
                      required
                    />
                  </div>

                  {/* ✅ HÖREN fields with CorrectAnswersInput */}
                  {task.taskType === "HOREN" && (
                    <>
                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Audio Reference (e.g., "Audio 3.2 from Kursbuch")
                        </label>
                        <input
                          type="text"
                          value={task.audioReference}
                          onChange={(e) =>
                            updateTask(index, "audioReference", e.target.value)
                          }
                          placeholder="Audio 3.2 from Kursbuch"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                          style={{ fontSize: "16px" }}
                        />
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Correct Answers * (Click "Add Answer" to add more)
                        </label>
                        <CorrectAnswersInput
                          taskIndex={index}
                          taskType={task.taskType}
                          correctAnswer={task.correctAnswer}
                          onUpdate={(value) =>
                            updateTask(index, "correctAnswer", value)
                          }
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Each answer = one question. Students will see
                          numbered questions (1, 2, 3...)
                        </p>
                      </div>
                    </>
                  )}

                  {/* ✅ ERGÄNZUNGEN fields with CorrectAnswersInput */}
                  {task.taskType === "ERGANZUNGEN" && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Correct Answers * (Click "Add Answer" to add more)
                      </label>
                      <CorrectAnswersInput
                        taskIndex={index}
                        taskType={task.taskType}
                        correctAnswer={task.correctAnswer}
                        onUpdate={(value) =>
                          updateTask(index, "correctAnswer", value)
                        }
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Each answer = one blank to fill. Students will see
                        numbered blanks (1, 2, 3...)
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || getTotalPoints() !== 20}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave />
                  Create Homework
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/homework")}
              disabled={loading}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminCreateHomeworkPage;
